import { motion, AnimatePresence } from "framer-motion";
import { X, MessageCircle, MapPin, CreditCard, User, StickyNote } from "lucide-react";
import { DEMO_RESTAURANT } from "@/data/demoMenu";

const brl = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });

const Field = ({ icon: Icon, ...props }) => (
    <div className="flex items-center gap-2.5 rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-3 focus-within:border-[#EA1D2C]/60 focus-within:bg-white">
        <Icon size={16} className="shrink-0 text-gray-400" />
        <input
            {...props}
            className="w-full bg-transparent text-sm text-gray-800 outline-none placeholder:text-gray-400"
        />
    </div>
);

export const CartPanel = ({ open, lines, subtotal, form, setForm, waUrl, onClose }) => {
    const total = subtotal + DEMO_RESTAURANT.taxaEntrega;
    return (
        <AnimatePresence>
            {open && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 z-[70] bg-black/50"
                    />
                    <motion.aside
                        data-testid="demo-cart-panel"
                        initial={{ x: "100%" }}
                        animate={{ x: 0 }}
                        exit={{ x: "100%" }}
                        transition={{ type: "spring", damping: 30, stiffness: 300 }}
                        className="fixed inset-y-0 right-0 z-[80] flex w-full max-w-md flex-col bg-white shadow-2xl"
                    >
                        <div className="flex items-center justify-between border-b border-gray-100 p-5">
                            <h2 className="text-lg font-extrabold text-gray-900">Sua Sacola</h2>
                            <button data-testid="demo-cart-close" onClick={onClose} className="rounded-full border border-gray-200 p-2 text-gray-500" aria-label="Fechar sacola">
                                <X size={17} />
                            </button>
                        </div>

                        <div className="flex-1 space-y-2.5 overflow-y-auto p-5">
                            {lines.map((l) => (
                                <div key={l.id} className="flex items-center justify-between rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
                                    <span className="text-sm text-gray-700">
                                        <b className="text-[#EA1D2C]">{l.qty}x</b> {l.nome}
                                    </span>
                                    <span className="text-sm font-bold text-gray-700">{brl.format(l.preco * l.qty)}</span>
                                </div>
                            ))}

                            <div className="space-y-2.5 pt-3">
                                <Field icon={User} data-testid="demo-input-nome" placeholder="Seu nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
                                <Field icon={MapPin} data-testid="demo-input-endereco" placeholder="Endereço de entrega" value={form.endereco} onChange={(e) => setForm({ ...form, endereco: e.target.value })} />
                                <div className="flex items-center gap-2.5 rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-1">
                                    <CreditCard size={16} className="shrink-0 text-gray-400" />
                                    <select
                                        data-testid="demo-select-pagamento"
                                        value={form.pagamento}
                                        onChange={(e) => setForm({ ...form, pagamento: e.target.value })}
                                        className="w-full bg-transparent py-2.5 text-sm text-gray-800 outline-none"
                                    >
                                        <option>Pix</option>
                                        <option>Dinheiro</option>
                                        <option>Cartão na entrega</option>
                                    </select>
                                </div>
                                {form.pagamento === "Dinheiro" && (
                                    <Field icon={CreditCard} data-testid="demo-input-troco" placeholder="Troco para quanto? (ex: R$ 100)" value={form.troco} onChange={(e) => setForm({ ...form, troco: e.target.value })} />
                                )}
                                <Field icon={StickyNote} data-testid="demo-input-obs" placeholder="Observações (ex: sem cebola)" value={form.obs} onChange={(e) => setForm({ ...form, obs: e.target.value })} />
                            </div>
                        </div>

                        <div className="space-y-2 border-t border-gray-100 p-5">
                            <div className="flex justify-between text-sm text-gray-500">
                                <span>Subtotal</span>
                                <span data-testid="demo-subtotal">{brl.format(subtotal)}</span>
                            </div>
                            <div className="flex justify-between text-sm text-gray-500">
                                <span>Taxa de entrega</span>
                                <span>{brl.format(DEMO_RESTAURANT.taxaEntrega)}</span>
                            </div>
                            <div className="flex justify-between border-t border-gray-100 pt-2.5 text-base font-extrabold text-gray-900">
                                <span>Total</span>
                                <span data-testid="demo-total">{brl.format(total)}</span>
                            </div>
                            <a
                                href={form.nome.trim() ? waUrl : undefined}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="demo-checkout-button"
                                aria-disabled={!form.nome.trim()}
                                className={`mt-2 flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-bold transition-all duration-300 ${
                                    form.nome.trim()
                                        ? "bg-[#25D366] text-white hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgba(37,211,102,0.4)]"
                                        : "cursor-not-allowed bg-gray-100 text-gray-400"
                                }`}
                            >
                                <MessageCircle size={17} strokeWidth={2.5} />
                                Enviar Pedido no WhatsApp
                            </a>
                            {!form.nome.trim() && (
                                <p className="text-center text-xs text-gray-400">Preencha seu nome para enviar o pedido</p>
                            )}
                        </div>
                    </motion.aside>
                </>
            )}
        </AnimatePresence>
    );
};
